import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { useSession } from '../session';
import { useNotifications } from '../notifications/NotificationSystem';
import { notificationService } from '../../services/api/notificationService';
import './Navigation.css';

export const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const session = useSession();
  const notifications = useNotifications();
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);

  const isActive = (path: string) => {
    if (path === '/trade' && (location.pathname === '/' || location.pathname === '/trade')) {
      return true;
    }
    return location.pathname === path;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const getOpenPositionsCount = () => {
    // This would come from actual state/API - hardcoded for demo
    return 3;
  };

  const handleAccountSwitch = (accountId: string) => {
    session.switchAccount(accountId);
    setShowAccountDropdown(false);
  };

  const formatBalance = (balance: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(balance);
  };

  const getDisplayAccountId = (accountId: string) => {
    // Format account ID for display (e.g., show last 8 digits)
    return accountId.length > 8 ? `MT${accountId.slice(-8)}` : accountId;
  };

  const handleLogout = () => {
    session.logout();
    setShowAccountDropdown(false);
  };

  if (isMobile) {
    return (
      <>
        {/* Mobile Header */}
        <header className="mobile-header">
          <div className="header-left">
            <button className="deposit-btn">Deposit</button>
          </div>
        <div className="header-right">
          <div className="account-switcher">
            {session.isAuthenticated && session.currentAccount ? (
              <>
                <div className="account-number">{getDisplayAccountId(session.currentAccount.accountId)}</div>
                <div className="account-balance">
                  {formatBalance(session.currentAccount.balance, session.currentAccount.currency)}
                </div>
              </>
            ) : (
              <>
                <div className="account-number">Not Connected</div>
                <div className="account-balance">--</div>
              </>
            )}
          </div>
          <div className="notification-icon" style={{ position: 'relative' }}>
            <button
              onClick={() => setShowNotificationDropdown(!showNotificationDropdown)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '6px',
                borderRadius: '4px',
                position: 'relative'
              }}
            >
              🔔
              {notifications.unreadCount > 0 && (
                <div className="notification-badge" style={{
                  position: 'absolute',
                  top: '-2px',
                  right: '-2px',
                  background: '#ff4757',
                  color: 'white',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  fontSize: '11px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}>
                  {notifications.unreadCount > 99 ? '99+' : notifications.unreadCount}
                </div>
              )}
            </button>

            {showNotificationDropdown && (
              <div style={{
                position: 'fixed',
                top: '60px',
                left: '8px',
                right: '8px',
                background: '#2a2a2a',
                border: '1px solid #444',
                borderRadius: '4px',
                zIndex: 1000,
                maxHeight: '70vh',
                overflowY: 'auto'
              }}>
                <div style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #444',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h3 style={{ margin: 0, fontSize: '16px', color: 'white' }}>
                    Notifications {notifications.unreadCount > 0 && `(${notifications.unreadCount})`}
                  </h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {notifications.unreadCount > 0 && (
                      <button
                        onClick={() => notifications.markAllAsRead()}
                        style={{
                          background: 'none',
                          border: '1px solid #555',
                          color: '#ccc',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Mark all read
                      </button>
                    )}
                    <button
                      onClick={() => setShowNotificationDropdown(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#888',
                        fontSize: '18px',
                        cursor: 'pointer',
                        padding: '4px'
                      }}
                    >
                      ×
                    </button>
                  </div>
                </div>

                <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                  {notifications.apiNotifications.length === 0 ? (
                    <div style={{
                      padding: '20px',
                      textAlign: 'center',
                      color: '#888',
                      fontSize: '14px'
                    }}>
                      No notifications
                    </div>
                  ) : (
                    notifications.apiNotifications.slice(0, 10).map((notification) => {
                      const formattedNotification = notificationService.formatNotificationForUI(notification);
                      
                      return (
                        <div
                          key={notification.id}
                          style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid #333',
                            background: notification.read ? 'transparent' : '#1a1a1a'
                          }}
                          onClick={() => {
                            if (!notification.read) {
                              notifications.markAsRead(notification.id);
                            }
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px'
                          }}>
                            <span style={{ fontSize: '16px' }}>
                              {formattedNotification.icon}
                            </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{
                                fontWeight: notification.read ? 'normal' : 'bold',
                                fontSize: '14px',
                                color: notification.read ? '#ccc' : 'white',
                                marginBottom: '4px'
                              }}>
                                {notification.title}
                              </div>
                              <div style={{
                                fontSize: '13px',
                                color: '#888',
                                lineHeight: '1.3',
                                marginBottom: '4px'
                              }}>
                                {notification.message}
                              </div>
                              <div style={{
                                fontSize: '12px',
                                color: '#666'
                              }}>
                                {formattedNotification.formattedTime}
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                notifications.deleteNotification(notification.id);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#666',
                                cursor: 'pointer',
                                padding: '4px',
                                fontSize: '16px'
                              }}
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        </header>

        {/* Mobile Bottom Navigation */}
        <nav className="bottom-navigation">
          <button 
            className={`nav-tab ${isActive('/trade') ? 'active' : ''}`}
            onClick={() => handleNavigation('/trade')}
          >
            <span className="nav-icon">📊</span>
            <span className="nav-label">Trade Room</span>
          </button>
          <button 
            className={`nav-tab ${isActive('/account') ? 'active' : ''}`}
            onClick={() => handleNavigation('/account')}
          >
            <span className="nav-icon">👤</span>
            <span className="nav-label">Account</span>
          </button>
          <button 
            className={`nav-tab ${isActive('/portfolio') ? 'active' : ''}`}
            onClick={() => handleNavigation('/portfolio')}
          >
            <span className="nav-icon">📋</span>
            <span className="nav-label">Portfolio</span>
            {getOpenPositionsCount() > 0 && (
              <span className="nav-badge">{getOpenPositionsCount()}</span>
            )}
          </button>
        </nav>
      </>
    );
  }

  // Desktop Navigation
  return (
    <header className="navigation">
      <div className="header-left">
        <div className="logo">Options Core</div>
        <nav className="nav-tabs">
          <button 
            className={`nav-tab ${isActive('/trade') ? 'active' : ''}`}
            onClick={() => handleNavigation('/trade')}
          >
            Trade Room
          </button>
          <button 
            className={`nav-tab ${isActive('/account') ? 'active' : ''}`}
            onClick={() => handleNavigation('/account')}
          >
            Account
          </button>
          <button 
            className={`nav-tab ${isActive('/portfolio') ? 'active' : ''}`}
            onClick={() => handleNavigation('/portfolio')}
          >
            Portfolio
          </button>
        </nav>
      </div>
      <div className="header-right">
        <button className="deposit-btn">Deposit</button>
        <div className="account-switcher" style={{ position: 'relative' }}>
          {session.loading ? (
            <div style={{ color: '#888', fontSize: '14px' }}>Loading...</div>
          ) : session.isAuthenticated && session.currentAccount ? (
            <>
              <button 
                className="account-display"
                onClick={() => setShowAccountDropdown(!showAccountDropdown)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: 'inherit', 
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>{getDisplayAccountId(session.currentAccount.accountId)}</span>
                <span>{formatBalance(session.currentAccount.balance, session.currentAccount.currency)}</span>
                <span style={{ transform: showAccountDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
              </button>
              
              {showAccountDropdown && (
                <div 
                  className="account-dropdown"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    background: '#2a2a2a',
                    border: '1px solid #444',
                    borderRadius: '4px',
                    minWidth: '220px',
                    zIndex: 1000,
                    marginTop: '4px'
                  }}
                >
                  {session.accounts.length > 1 && session.accounts.map((account) => (
                    <button
                      key={account.accountId}
                      onClick={() => handleAccountSwitch(account.accountId)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        background: account.accountId === session.currentAccount?.accountId ? '#444' : 'transparent',
                        border: 'none',
                        color: 'white',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#444'}
                      onMouseLeave={(e) => e.currentTarget.style.background = account.accountId === session.currentAccount?.accountId ? '#444' : 'transparent'}
                    >
                      <span>{getDisplayAccountId(account.accountId)}</span>
                      <span>{formatBalance(account.balance, account.currency)}</span>
                    </button>
                  ))}
                  <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #444' }} />
                  <button
                    onClick={handleLogout}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      background: 'transparent',
                      border: 'none',
                      color: '#ff6b6b',
                      textAlign: 'left',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#444'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    Logout
                  </button>
                </div>
              )}
            </>
          ) : session.error ? (
            <div style={{ color: '#ff6b6b', fontSize: '14px' }}>Connection Error</div>
          ) : (
            <div style={{ color: '#888', fontSize: '14px' }}>Connecting...</div>
          )}
        </div>
        <div className="notification-icon" style={{ position: 'relative' }}>
          <button
            onClick={() => setShowNotificationDropdown(!showNotificationDropdown)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '4px',
              position: 'relative'
            }}
          >
            🔔
            {notifications.unreadCount > 0 && (
              <div className="notification-badge" style={{
                position: 'absolute',
                top: '0px',
                right: '0px',
                background: '#ff4757',
                color: 'white',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold'
              }}>
                {notifications.unreadCount > 99 ? '99+' : notifications.unreadCount}
              </div>
            )}
          </button>

          {showNotificationDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              background: '#2a2a2a',
              border: '1px solid #444',
              borderRadius: '4px',
              minWidth: '350px',
              maxWidth: '400px',
              zIndex: 1000,
              marginTop: '4px',
              maxHeight: '400px',
              overflowY: 'auto'
            }}>
              <div style={{
                padding: '12px 16px',
                borderBottom: '1px solid #444',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{ margin: 0, fontSize: '16px', color: 'white' }}>
                  Notifications {notifications.unreadCount > 0 && `(${notifications.unreadCount})`}
                </h3>
                {notifications.unreadCount > 0 && (
                  <button
                    onClick={() => {
                      notifications.markAllAsRead();
                      setShowNotificationDropdown(false);
                    }}
                    style={{
                      background: 'none',
                      border: '1px solid #555',
                      color: '#ccc',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    Mark all read
                  </button>
                )}
              </div>

              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {notifications.apiNotifications.length === 0 ? (
                  <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#888',
                    fontSize: '14px'
                  }}>
                    No notifications
                  </div>
                ) : (
                  notifications.apiNotifications.slice(0, 10).map((notification) => {
                    const formattedNotification = notificationService.formatNotificationForUI(notification);
                    
                    return (
                      <div
                        key={notification.id}
                        style={{
                          padding: '12px 16px',
                          borderBottom: '1px solid #333',
                          cursor: 'pointer',
                          background: notification.read ? 'transparent' : '#1a1a1a',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#333'}
                        onMouseLeave={(e) => e.currentTarget.style.background = notification.read ? 'transparent' : '#1a1a1a'}
                        onClick={() => {
                          if (!notification.read) {
                            notifications.markAsRead(notification.id);
                          }
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px'
                        }}>
                          <span style={{ fontSize: '16px' }}>
                            {formattedNotification.icon}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontWeight: notification.read ? 'normal' : 'bold',
                              fontSize: '14px',
                              color: notification.read ? '#ccc' : 'white',
                              marginBottom: '4px'
                            }}>
                              {notification.title}
                            </div>
                            <div style={{
                              fontSize: '13px',
                              color: '#888',
                              lineHeight: '1.3',
                              marginBottom: '4px'
                            }}>
                              {notification.message}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#666'
                            }}>
                              {formattedNotification.formattedTime}
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              notifications.deleteNotification(notification.id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#666',
                              cursor: 'pointer',
                              padding: '4px',
                              fontSize: '16px'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#999'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {notifications.apiNotifications.length > 10 && (
                <div style={{
                  padding: '12px 16px',
                  borderTop: '1px solid #444',
                  textAlign: 'center'
                }}>
                  <button
                    onClick={() => {
                      setShowNotificationDropdown(false);
                      // Navigate to full notifications page if it exists
                    }}
                    style={{
                      background: 'none',
                      border: '1px solid #555',
                      color: '#ccc',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    View all notifications
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navigation;
